import { TFunction } from 'i18next';
import * as yup from 'yup';

import { requiredMsgFunc } from '../EditInsurancePage/schema';

import * as api from '../../../../../api';
import { isNumberValid } from '../../../../../utils/phoneNumber';
import { EMPLOYMENT_STATUS_SHOW_EMPLOYER } from 'common/dist/utils/previsitFormField';

export type SubscriberUpload = Omit<
  api.PrevisitPatientInsurance.PrevisitSubscriber,
  'id' | 'newlyAdded' | 'patientRelationshipToPrimaryPolicyholder'
> & {
  subscriberId: string | null;
  patientRelationshipToPrimaryPolicyholder: api.Common.Relationship | null;
};

const generatePhoneInvalidMessage = (t: TFunction) =>
  t('phoneInvalid', { ns: 'editInsurancePage' });

export const NEW_SUBSCRIBER_SCHEMA = (
  t: TFunction,
  employmentStatuses: api.PracticePreference.EmploymentStatus[],
  employers: (typeof api.Employer)[],
  newSubscriber: boolean,
) => {
  const requiredMsg = requiredMsgFunc(t);

  return yup.lazy((values: SubscriberUpload) => {
    return yup.object().shape({
      subscriberId: yup.string().min(1).required(),
      primaryPolicyHolderFirstName: yup.string().when(() => {
        return newSubscriber
          ? yup.string().min(1).required(requiredMsg)
          : yup.string().notRequired().nullable();
      }),
      primaryPolicyHolderLastName: yup
        .string()
        .when(() =>
          newSubscriber
            ? yup.string().min(1).required(requiredMsg)
            : yup.string().notRequired().nullable(),
        ),
      primaryPolicyHolderDob: yup
        .date()
        .when(() =>
          newSubscriber
            ? yup.date().required(requiredMsg)
            : yup.date().notRequired().nullable(),
        ),
      primaryPolicyHolderSex: yup
        .string()
        .when(() =>
          newSubscriber
            ? yup
                .string()
                .oneOf(api.Previsit.DefaultSexes)
                .required(requiredMsg)
            : yup.string().notRequired().nullable(),
        ),
      primaryPolicyHolderAddress: yup
        .object()
        .when(() =>
          newSubscriber
            ? yup.object().required(requiredMsg)
            : yup.object().notRequired().nullable(),
        ),
      primaryPolicyHolderHomePhone: yup.string().when(() =>
        newSubscriber
          ? yup
              .string()
              .test('is-valid-phone', generatePhoneInvalidMessage(t), () =>
                isNumberValid(values.primaryPolicyHolderHomePhone ?? ''),
              )
              .notRequired()
              .nullable()
          : yup.string().notRequired().nullable(),
      ),
      primaryPolicyHolderEmploymentStatus: yup
        .string()
        .when(() =>
          newSubscriber && employmentStatuses.length > 0
            ? yup.string().oneOf(employmentStatuses).required(requiredMsg)
            : yup.string().notRequired().nullable(),
        ),
      primaryPolicyHolderEmployer: yup
        .string()
        .when(() =>
          newSubscriber &&
          values.primaryPolicyHolderEmploymentStatus &&
          EMPLOYMENT_STATUS_SHOW_EMPLOYER.includes(
            values.primaryPolicyHolderEmploymentStatus,
          ) &&
          employers.length > 0
            ? yup.string().required(requiredMsg)
            : yup.string().notRequired().nullable(),
        ),
      patientRelationshipToPrimaryPolicyholder: yup
        .string()
        .when(() =>
          newSubscriber
            ? yup
                .string()
                .oneOf(Object.values(api.Common.Relationship))
                .required(requiredMsg)
            : yup.string().notRequired().nullable(),
        ),
    });
  });
};
